<template>
  <div
    v-if="groups.length"
    class="pd-sku-picker"
    :class="{ 'pd-sku-picker--drawer': isDrawer }"
  >
    <template v-if="!stepped">
      <div class="pd-sku-axis">
        <div class="pd-sku-axis-label">
          <span class="pd-sku-step">1</span>
          {{ $t('pdOption') }}
        </div>
        <div
          class="pd-sku-chips"
          :class="{ 'pd-sku-chips--scroll': !isDrawer }"
        >
          <button
            v-for="sku in flatSkus"
            :key="skuKey(sku)"
            type="button"
            class="pd-sku-chip"
            :class="{ 'is-active': isActiveSku(sku) }"
            @click="$emit('select-sku', sku)"
          >
            <img
              v-if="skuImg(sku)"
              :src="skuImg(sku)"
              alt=""
              class="pd-sku-chip-img"
              @error="e => { e.target.src = '/img/no-image.png' }"
            >
            <span class="pd-sku-chip-text">{{ skuLabel(sku) }}</span>
          </button>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="pd-sku-axis">
        <div class="pd-sku-axis-label">
          <span class="pd-sku-step">1</span>
          {{ $t('pdSelectProduct') }}
          <span
            v-if="activeGroup"
            class="pd-sku-axis-value"
          >{{ activeGroup.label }}</span>
          <span
            v-else
            class="pd-sku-axis-muted"
          >{{ $t('pdNotSelected') }}</span>
        </div>
        <div class="pd-sku-colors">
          <button
            v-for="group in groups"
            :key="group.key"
            type="button"
            class="pd-sku-color"
            :class="{ 'is-active': group.key === colorKey }"
            :title="group.label"
            @click="$emit('pick-color', group.key)"
          >
            <img
              v-if="group.image"
              :src="group.image"
              alt=""
              @error="e => { e.target.src = '/img/no-image.png' }"
            >
            <span class="pd-sku-color-name">{{ group.label }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="hasSize"
        class="pd-sku-axis"
      >
        <div class="pd-sku-axis-label">
          <span class="pd-sku-step">2</span>
          {{ $t('pdSelectStyle') }}
          <span class="pd-sku-axis-hint">{{ $t('pdStyleHint') }}</span>
          <span
            v-if="size"
            class="pd-sku-axis-value"
          >{{ size }}</span>
          <span
            v-else
            class="pd-sku-axis-muted"
          >{{ $t('pdSelectStyleBelow') }}</span>
        </div>
        <div
          class="pd-sku-sizes"
          :class="{ 'pd-sku-sizes--scroll': !isDrawer }"
        >
          <button
            v-for="sz in displaySizes"
            :key="sz"
            type="button"
            class="pd-sku-size"
            :class="{ 'is-active': size === sz }"
            :disabled="!sizeAvailable(sz)"
            @click="$emit('pick-size', sz)"
          >
            <span class="pd-sku-size-text">{{ sz }}</span>
          </button>
        </div>
      </div>
    </template>

    <div
      v-if="isDrawer"
      class="pd-sku-axis pd-sku-qty-axis"
      :class="{ 'is-disabled': !ready }"
    >
      <div class="pd-sku-axis-label">
        <span class="pd-sku-step">{{ hasSize && stepped ? 3 : 2 }}</span>
        {{ $t('pdQuantity') }}
      </div>
      <div class="pd-sku-draft-row">
        <div class="pd-sku-qty">
          <button
            type="button"
            class="pd-sku-qty-btn"
            :disabled="!ready || draftQty <= 1"
            @click="$emit('set-qty', draftQty - 1)"
          >−</button>
          <input
            :value="draftQty"
            type="text"
            inputmode="numeric"
            class="pd-sku-qty-input"
            :disabled="!ready"
            @input="onQtyInput"
          >
          <button
            type="button"
            class="pd-sku-qty-btn"
            :disabled="!ready"
            @click="$emit('set-qty', draftQty + 1)"
          >+</button>
        </div>
      </div>
      <div
        v-if="!ready"
        class="pd-sku-hint"
      >
        {{ $t('pdCompleteSelectionHint') }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PdSkuPicker',
  props: {
    variant: {
      type: String,
      default: 'page',
    },
    groups: {
      type: Array,
      default: () => [],
    },
    flatSkus: {
      type: Array,
      default: () => [],
    },
    colorKey: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: '',
    },
    hasSize: {
      type: Boolean,
      default: false,
    },
    stepped: {
      type: Boolean,
      default: false,
    },
    draftQty: {
      type: Number,
      default: 1,
    },
    ready: {
      type: Boolean,
      default: false,
    },
    skuImg: {
      type: Function,
      default: () => '',
    },
    skuLabel: {
      type: Function,
      default: () => '',
    },
    skuKey: {
      type: Function,
      default: () => '',
    },
    skuQty: {
      type: Function,
      default: () => 0,
    },
    isActiveSku: {
      type: Function,
      default: () => false,
    },
    sizeAvailable: {
      type: Function,
      default: () => true,
    },
    sizeQty: {
      type: Function,
      default: () => 0,
    },
  },
  computed: {
    isDrawer() {
      return this.variant === 'drawer'
    },
    activeGroup() {
      return this.groups.find(g => g.key === this.colorKey) || null
    },
    displaySizes() {
      if (this.colorKey && this.activeGroup) return this.activeGroup.sizes || []
      const seen = {}
      const list = []
      this.groups.forEach(g => {
        (g.sizes || []).forEach(sz => {
          if (!seen[sz]) {
            seen[sz] = true
            list.push(sz)
          }
        })
      })
      return list
    },
  },
  methods: {
    groupQty(group) {
      return (group.skus || []).reduce((sum, sku) => sum + (this.skuQty(sku) || 0), 0)
    },
    onQtyInput(e) {
      const n = Number(String(e.target.value || '').replace(/\D/g, ''))
      this.$emit('set-qty', Number.isFinite(n) && n > 0 ? n : 1)
    },
    onHScrollWheel(e) {
      const el = e.currentTarget
      if (!el || el.scrollWidth <= el.clientWidth + 1) return
      const useY = Math.abs(e.deltaY) >= Math.abs(e.deltaX)
      const delta = useY ? e.deltaY : e.deltaX
      if (!delta) return
      const max = el.scrollWidth - el.clientWidth
      const next = Math.min(max, Math.max(0, el.scrollLeft + delta))
      if (next === el.scrollLeft) return
      e.preventDefault()
      el.scrollLeft = next
    },
    bindHScroll() {
      const nodes = this.$el && this.$el.querySelectorAll
        ? this.$el.querySelectorAll('.pd-sku-colors, .pd-sku-sizes--scroll, .pd-sku-chips--scroll')
        : []
      nodes.forEach(el => {
        if (el.dataset.hscrollBound) return
        el.dataset.hscrollBound = '1'
        el.addEventListener('wheel', this.onHScrollWheel, { passive: false })
      })
    },
  },
  mounted() {
    this.bindHScroll()
  },
  updated() {
    this.bindHScroll()
  },
  beforeDestroy() {
    const nodes = this.$el && this.$el.querySelectorAll
      ? this.$el.querySelectorAll('.pd-sku-colors, .pd-sku-sizes--scroll, .pd-sku-chips--scroll')
      : []
    nodes.forEach(el => {
      el.removeEventListener('wheel', this.onHScrollWheel)
    })
  },
}
</script>
